import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";
import Logo from "../components/Logo";

function TextField({
  id,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  autoComplete,
  required = true,
}) {
  return (
    <label className="block" htmlFor={id}>
      <span className="mb-2 block text-sm font-bold text-ink">{label}</span>
      <input
        id={id}
        name={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        className="h-12 w-full rounded-xl border border-ink/15 bg-white px-4 text-sm text-ink outline-none transition placeholder:text-slate/60 focus:border-ocean focus:ring-4 focus:ring-ocean/10"
      />
    </label>
  );
}

function PasswordField({ id, label, value, onChange, autoComplete }) {
  const [visible, setVisible] = useState(false);

  return (
    <label className="block" htmlFor={id}>
      <span className="mb-2 block text-sm font-bold text-ink">{label}</span>
      <span className="relative block">
        <input
          id={id}
          name={id}
          type={visible ? "text" : "password"}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          required
          className="h-12 w-full rounded-xl border border-ink/15 bg-white px-4 pr-12 text-sm text-ink outline-none transition placeholder:text-slate/60 focus:border-ocean focus:ring-4 focus:ring-ocean/10"
        />
        <button
          type="button"
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate transition hover:bg-mist hover:text-ocean focus:outline-none focus:ring-2 focus:ring-ocean/30"
        >
          {visible ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </span>
    </label>
  );
}

function AuthLayout({ children }) {
  return (
    <main className="relative min-h-screen overflow-hidden bg-mist px-4 py-8 text-ink sm:px-6 sm:py-12">
      <div className="pointer-events-none absolute left-1/2 top-0 h-64 w-[min(100%,720px)] -translate-x-1/2 rounded-full bg-ocean/10 blur-3xl" />
      <div className="relative mx-auto flex min-h-[calc(100vh-4rem)] w-full flex-col justify-center" style={{ maxWidth: "460px" }}>
        <div className="mb-8 flex flex-col items-center">
          <Logo />
          <Link to="/" className="mt-3 text-xs font-bold text-slate transition hover:text-ocean">
            Back to website
          </Link>
        </div>
        <section className="w-full rounded-[1.35rem] border border-ink/10 border-t-2 border-t-ocean/70 bg-white p-6 shadow-[0_22px_60px_rgba(6,28,61,.10)] sm:p-9">
          {children}
        </section>
        <p className="mt-6 text-center text-xs text-slate">
          © {new Date().getFullYear()} The Bridge by VA
        </p>
      </div>
    </main>
  );
}

function FormMessage({ children, error = false }) {
  return (
    <p
      role={error ? "alert" : "status"}
      className={`text-sm leading-6 ${error ? "font-bold text-red-600" : "text-slate"}`}
    >
      {children}
    </p>
  );
}

export function Login() {
  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [password, setPassword] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [recoveryRequested, setRecoveryRequested] = useState(false);

  function handleSubmit(event) {
    event.preventDefault();
    setRecoveryRequested(false);
    setSubmitted(true);
  }

  return (
    <AuthLayout>
      <div className="text-center">
        <p className="eyebrow">Customer portal</p>
        <h1 className="mt-3 font-display text-3xl font-extrabold tracking-[-.045em] text-ink">
          Sign in
        </h1>
        <p className="mt-2 text-sm leading-6 text-slate">
          Enter your details to continue.
        </p>
      </div>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
        <TextField
          id="emailOrPhone"
          label="Email or phone number"
          value={emailOrPhone}
          onChange={(event) => setEmailOrPhone(event.target.value)}
          placeholder="you@example.com"
          autoComplete="username"
        />
        <PasswordField
          id="loginPassword"
          label="Password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
        />
        <div className="flex items-center justify-between gap-4 text-sm">
          <label className="inline-flex items-center gap-2 text-slate">
            <input type="checkbox" className="h-4 w-4 rounded border-ink/20 accent-ocean" />
            Remember me
          </label>
          <button
            type="button"
            onClick={() => {
              setSubmitted(false);
              setRecoveryRequested(true);
            }}
            className="font-bold text-ocean hover:text-ink"
          >
            Forgot password?
          </button>
        </div>
        <button
          type="submit"
          className="h-12 w-full rounded-full bg-ocean px-5 text-sm font-bold text-white shadow-[0_10px_28px_rgba(29,159,233,.25)] transition hover:bg-[#1387cb] focus:outline-none focus:ring-2 focus:ring-ocean/40 focus:ring-offset-2"
        >
          Sign in
        </button>
        {submitted && <FormMessage>Ready to continue with secure sign-in.</FormMessage>}
        {recoveryRequested && <FormMessage>We’ll send recovery instructions to your verified contact.</FormMessage>}
      </form>

      <p className="mt-7 text-center text-sm text-slate">
        Don’t have an account? <Link to="/signup" className="font-bold text-ocean hover:text-ink">Create one</Link>
      </p>
    </AuthLayout>
  );
}

export function SignUp() {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [formError, setFormError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  function updateField(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setFormError("");
    setSubmitted(false);
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (!form.email && !form.phone) {
      setFormError("Enter an email address or phone number.");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setFormError("Passwords do not match.");
      return;
    }
    setFormError("");
    setSubmitted(true);
  }

  return (
    <AuthLayout>
      <div className="text-center">
        <p className="eyebrow">Customer portal</p>
        <h1 className="mt-3 font-display text-3xl font-extrabold tracking-[-.045em] text-ink">
          Create an account
        </h1>
        <p className="mt-2 text-sm leading-6 text-slate">
          Fill in your details to get started.
        </p>
      </div>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
        <div className="grid gap-5 sm:grid-cols-2">
          <TextField id="firstName" label="First name" value={form.firstName} onChange={updateField} autoComplete="given-name" />
          <TextField id="lastName" label="Last name" value={form.lastName} onChange={updateField} autoComplete="family-name" />
        </div>
        <TextField id="email" label="Email address" type="email" value={form.email} onChange={updateField} placeholder="you@example.com" autoComplete="email" required={false} />
        <TextField id="phone" label="Phone number" type="tel" value={form.phone} onChange={updateField} placeholder="+234 ..." autoComplete="tel" required={false} />
        <p className="-mt-2 text-xs text-slate">Use at least one contact method.</p>
        <PasswordField id="password" label="Password" value={form.password} onChange={updateField} autoComplete="new-password" />
        <PasswordField id="confirmPassword" label="Confirm password" value={form.confirmPassword} onChange={updateField} autoComplete="new-password" />
        <label className="flex items-start gap-3 text-sm leading-6 text-slate">
          <input type="checkbox" required className="mt-1 h-4 w-4 shrink-0 rounded border-ink/20 accent-ocean" />
          <span>
            I agree to the <Link className="font-bold text-ocean hover:text-ink" to="/legal/terms">Terms</Link> and <Link className="font-bold text-ocean hover:text-ink" to="/legal/privacy">Privacy policy</Link>.
          </span>
        </label>
        <button
          type="submit"
          className="h-12 w-full rounded-full bg-ocean px-5 text-sm font-bold text-white shadow-[0_10px_28px_rgba(29,159,233,.25)] transition hover:bg-[#1387cb] focus:outline-none focus:ring-2 focus:ring-ocean/40 focus:ring-offset-2"
        >
          Create account
        </button>
        {formError && <FormMessage error>{formError}</FormMessage>}
        {submitted && <FormMessage>Ready to continue with contact verification.</FormMessage>}
      </form>

      <p className="mt-7 text-center text-sm text-slate">
        Already have an account? <Link to="/login" className="font-bold text-ocean hover:text-ink">Sign in</Link>
      </p>
    </AuthLayout>
  );
}
